import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { subscriptionPagePath } from "src/ui/navigation/dashboardRouting/dashboardRouting";
import { makeStyles } from "tss-react/mui";

import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { PrimaryButton } from "./CustomButton";

// The wrapper must never clamp or clip its content: the app scrolls in the drawer's content column,
// so a bounded, overflow-hidden wrapper would make everything below the fold unreachable on a page
// that is taller than the viewport. The flex properties only matter inside a `fitViewport` page,
// where they let a table fill the bounded height as usual.
//
// Nothing that covers the page may be `position: fixed` either. A fixed element scrolls with the
// viewport rather than with an ancestor, so the browser sends a wheel event over it to the document
// instead of to the scrolling column — the page then looks scrollable and refuses to move. The
// blocker therefore scrolls along with the content, and the centred card lets wheel events through
// (only its button keeps pointer events, because it has to stay clickable).
const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        position: "relative",
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },
    content: {
        flex: 1,
        minHeight: 0,
        width: "100%",
        pointerEvents: "none",
    },
    blur: { filter: "blur(5px)" },
    interactionBlocker: {
        position: "absolute",
        inset: 0,
        zIndex: 50,
        pointerEvents: "auto",
    },
    cardOverlay: {
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        pointerEvents: "none",
    },
    expiredCard: {
        maxWidth: 400,
        padding: 0,
    },
    renewButton: {
        pointerEvents: "auto",
    },
    expiredCardContent: {
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(8),
        gap: theme.spacing(2),
        alignItems: "center",
    },
}));

export const ExpiredWrapper = (props: React.PropsWithChildren<object>) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common"]);

    const isExpired = useCurrentSubscriptionExpired();
    const navigate = useNavigate();

    const onRenew = () => {
        void navigate(subscriptionPagePath);
    };

    if (!isExpired) {
        return <React.Fragment>{props.children}</React.Fragment>;
    }

    return (
        <Box className={classes.container}>
            <Box className={cx(classes.content, classes.blur)}>{props.children}</Box>
            <Box className={classes.interactionBlocker} aria-hidden />
            <Box className={classes.cardOverlay}>
                <SectionPanel className={classes.expiredCard}>
                    <Box className={classes.expiredCardContent}>
                        <Typography variant="h6" style={{ fontWeight: "bold" }}>
                            {t("common:section.expiredWrapper.title")}
                        </Typography>
                        <Typography align="center">{t("common:section.expiredWrapper.description")}</Typography>
                        <PrimaryButton className={classes.renewButton} onClick={onRenew}>
                            {t("common:section.expiredWrapper.button")}
                        </PrimaryButton>
                    </Box>
                </SectionPanel>
            </Box>
        </Box>
    );
};

export const withExpiredWrapper = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
    const ComponentWithExpiredWrapper: React.FC<P & React.PropsWithChildren<object>> = (props) => {
        const isExpired = useCurrentSubscriptionExpired();

        if (!isExpired) {
            return <WrappedComponent {...props} />;
        }

        return (
            <ExpiredWrapper>
                <WrappedComponent {...props} />
            </ExpiredWrapper>
        );
    };

    return ComponentWithExpiredWrapper;
};
